// The org-scope picker's rows: the in-scope orgs as a tree flattened into one
// list, each org directly above the orgs under it. A network is no longer just
// "head office + schools" — a head over regions over schools would be 67 names
// in one flat run, the regions' rollups scattered among the schools with
// nothing to say which school sits under which.
//
// Each row carries its `depth` (to indent by) and its `ancestors` (to keep a
// matched school's region in view while searching). An org whose parent is
// outside the caller's scope is a root — a regional coordinator's tree starts
// at their region.
//
// Siblings keep the server's order (by name), with aggregating orgs first: in
// a network the "(all schools)" rollups are the views most people come back
// for, and alphabetical order would bury them among the schools.
export function orgTreeRows(organizations) {
  const orgs = organizations || [];
  const inScope = new Set(orgs.map((o) => o.uuid));
  const childrenOf = childrenIndex(orgs);
  const roots = orgs.filter((o) => !inScope.has(o.parent));
  const isHead = (o) => childrenOf.has(o.uuid);
  const headsFirst = (list) =>
    [...list].sort((a, b) => Number(isHead(b)) - Number(isHead(a)));

  const rows = [];
  const walk = (org, depth, ancestors) => {
    rows.push({
      uuid: org.uuid,
      name: org.name,
      depth,
      ancestors,
      isHead: isHead(org),
    });
    headsFirst(childrenOf.get(org.uuid) || []).forEach((child) =>
      walk(child, depth + 1, ancestors.concat(org.uuid)),
    );
  };
  headsFirst(roots).forEach((root) => walk(root, 0, []));
  return rows;
}

// Parent uuid -> the orgs directly under it. Only parents with children get an
// entry, so `has(uuid)` is "heads something".
export function childrenIndex(organizations) {
  const childrenOf = new Map();
  (organizations || []).forEach((o) => {
    if (!o.parent) return;
    if (childrenOf.has(o.parent)) childrenOf.get(o.parent).push(o);
    else childrenOf.set(o.parent, [o]);
  });
  return childrenOf;
}

// `uuid` and the uuid of every org under it. Iterative, and a cycle (an admin
// edit gone wrong) ends the walk rather than the tab.
export function subtreeUuids(childrenOf, uuid) {
  const seen = new Set();
  const stack = [uuid];
  while (stack.length) {
    const u = stack.pop();
    if (!seen.has(u)) {
      seen.add(u);
      (childrenOf.get(u) || []).forEach((c) => stack.push(c.uuid));
    }
  }
  return [...seen];
}

// Search over those rows (or options spread from them), by NAME — the "(all
// schools)" suffix is the same on every rollup, so matching the label would
// hand back every region for "all". A matched school keeps its region above it
// (where it is is half of which one it is), a matched region brings its schools
// (searching a region is asking for them), and the top-level root is left out
// as context: it heads every result and says nothing about any of them.
//
// A region kept only for its matched school comes back flagged `context`, so
// the picker can set it back and land Enter on the school that was typed
// rather than on the region above it. Depths are re-based on the shallowest
// row left, so a result does not start one step in under a root it no longer
// shows.
export function filterOrgTreeRows(rows, query) {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  const hits = new Set(
    rows
      .filter((r) => String(r.name).toLowerCase().includes(q))
      .map((r) => r.uuid),
  );
  const context = new Set();
  rows.forEach((r) => {
    if (hits.has(r.uuid)) r.ancestors.forEach((a) => context.add(a));
  });
  const kept = rows
    .map((r) => {
      if (hits.has(r.uuid) || r.ancestors.some((a) => hits.has(a))) {
        return { ...r, context: false };
      }
      if (r.depth > 0 && context.has(r.uuid)) return { ...r, context: true };
      return null;
    })
    .filter(Boolean);
  const base = Math.min(...kept.map((r) => r.depth));
  return kept.map((r) => ({ ...r, depth: r.depth - base }));
}
